import React from 'react';
import { FormattedHTMLMessage, useIntl } from 'react-intl';
import { Counter } from '../../components';
import { useUnreadMessageCounters } from '../../hooks/useUnreadMessageCounters';
import { staticConfig } from '../../config/static';

import images from '../../assets/images/images';
import styles from './headertabs.module.scss';
import { Tab } from './Tab';
import { Image } from '../Image/Image';
import { useSelector } from 'react-redux';
import { IRootState } from '../../redux';

interface IHeaderTabsProps {
    display?: boolean;
    mailboxLink: string;
    overviewLink: string;
}

export const HeaderTabs = ({ display = true, mailboxLink = '', overviewLink = '' }: IHeaderTabsProps): JSX.Element => {
    const { unreadPublicMessageCount, unreadPrivateMessageCount } = useUnreadMessageCounters();
    const { formatMessage } = useIntl();
    const { isGreenlandSubscription } = useSelector(({ mailbox }: IRootState) => ({
        isGreenlandSubscription: mailbox.isGreenlandSubscription,
    }));

    const getTabText = () => {
        if (isGreenlandSubscription) {
            return {
                mailFromCompanies: <FormattedHTMLMessage id="NGDP_MAIL_FROM_COMPANIES_GREENLANDIC_TEXT" />,
                mailFromPublicAuthorities: (
                    <FormattedHTMLMessage id="NGDP_MAIL_FROM_PUBLIC_AUTHORITIES_GREENLANDIC_TEXT" />
                ),
            };
        } else {
            return {
                mailFromCompanies: <BreakAfterSecondSpace label={formatMessage({ id: 'MENU_MAIL_TITLE_PRIVATE' })} />,
                mailFromPublicAuthorities: (
                    <BreakAfterSecondSpace label={formatMessage({ id: 'MENU_MAIL_TITLE_PUBLIC' })} />
                ),
            };
        }
    };

    return display ? (
        <div className="d-flex flex-row w-100">
            {/* Element insert to align layout with classic */}
            <div className={`d-none d-lg-block ${styles.classicSpacer}`} />

            <nav className={styles.headerTabs}>
                <ul>
                    <Tab
                        className={`d-md-none ${isGreenlandSubscription ? styles.headerTabsFont : ''}`}
                        href={!staticConfig.isclassicunavailable ? overviewLink : '/notavailable/overview'}
                    >
                        <Image
                            src={images.home}
                            alt="TABS_MY_OVERVIEW"
                            className={staticConfig.isclassicunavailable ? 'activeimage' : ''}
                        />
                    </Tab>

                    <Tab
                        className={`d-none d-md-block ${isGreenlandSubscription ? styles.headerTabsFont : ''}`}
                        href={!staticConfig.isclassicunavailable ? overviewLink : '/notavailable/overview'}
                    >
                        <span>{formatMessage({ id: 'TABS_MY_OVERVIEW' })}</span>
                    </Tab>

                    <Tab
                        href={!staticConfig.isclassicunavailable ? mailboxLink : '/notavailable/mailbox'}
                        className={isGreenlandSubscription ? styles.headerTabsFont : ''}
                    >
                        {getTabText().mailFromCompanies}

                        <Counter count={unreadPrivateMessageCount} />
                    </Tab>

                    <Tab
                        href="/mailbox"
                        className={`${!staticConfig.isclassicunavailable ? styles.isActiveWithClassic : ''} ${
                            isGreenlandSubscription ? styles.headerTabsFont : ''
                        }`}
                    >
                        {getTabText().mailFromPublicAuthorities}

                        <Counter count={unreadPublicMessageCount} />
                    </Tab>
                </ul>
            </nav>
        </div>
    ) : null;
};

/**
 * Helper Component to force line breaks after second word in translation
 */
const BreakAfterSecondSpace = ({ label }: { label: string }): JSX.Element => {
    if (!label) {
        return null;
    }

    const labelParts = label.split(' ');

    if (labelParts.length < 3) {
        return <span>{labelParts.join(' ')}</span>;
    }

    const firstPart = labelParts.slice(0, 2).join(' ');
    const secondPart = labelParts.slice(2).join(' ');

    return (
        <span>
            {firstPart}
            <br />
            {secondPart}
        </span>
    );
};
